import React from 'react';

const readDraft = (key: string | null): string => {
  if (key === null || typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
};

/**
 * Reads/writes the Cara compose-box draft text to `localStorage` so the user
 * can navigate away (or open the app in another tab) without losing what
 * they typed.
 *
 * Pass a string `key` to persist; pass `null` to opt out (e.g. the inline
 * follow-up textarea in a live workspace doesn't need persistence).
 *
 * Cross-tab consistency: the hook re-reads from storage on `focus` and
 * `visibilitychange`, which covers the "I typed in another tab" case
 * without needing to subscribe to `storage` events (which only fire in
 * *other* tabs).
 */
export function useDraftPersistence(
  key: string | null,
  /** Reload the draft when the key changes; otherwise the text typed under the old key carries over. */
  options: { keepDraftsPerKey?: boolean } = {}
): {
  query: string;
  setQuery: (next: string) => void;
  /**
   * Synchronously clears both the in-memory draft and its `localStorage`
   * entry. Call this on successful submit / workspace creation so the
   * draft is removed *before* the parent navigates away — the
   * state-change-driven persistence effect would otherwise race against
   * unmount and leave a stale draft behind.
   */
  clearDraft: () => void;
} {
  const enabled = key !== null && typeof window !== 'undefined';

  const [draft, setDraft] = React.useState(() => ({ key, query: readDraft(key) }));
  // Reset before committing a different document, so its storage cannot receive the old draft.
  if (options.keepDraftsPerKey && draft.key !== key) setDraft({ key, query: readDraft(key) });
  const { query } = draft;
  const setQuery = React.useCallback((next: string) => setDraft({ key, query: next }), [key]);

  // Persist on every change (write empty string as a "clear" by removing the
  // key, which keeps localStorage tidy and avoids stale "" sticking around).
  React.useEffect(() => {
    if (!enabled) return;
    try {
      if (query.trim().length > 0) {
        window.localStorage.setItem(key, query);
      } else {
        window.localStorage.removeItem(key);
      }
    } catch {
      // Ignore quota / private-mode errors — persistence is best-effort.
    }
  }, [enabled, key, query]);

  // Re-hydrate from storage when the tab regains focus or becomes visible.
  // This is what lets a draft typed in tab A show up in tab B when the user
  // switches over without a full page reload.
  React.useEffect(() => {
    if (!enabled) return undefined;

    const restore = () => {
      try {
        const saved = window.localStorage.getItem(key);
        if (saved !== null) setQuery(saved);
      } catch {
        // Ignore — best-effort.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') restore();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', restore);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', restore);
    };
  }, [enabled, key, setQuery]);

  const clearDraft = React.useCallback(() => {
    // Wipe storage *first* so it stays cleared even if the component
    // unmounts (e.g. on post-submit navigation) before the persistence
    // effect would have run.
    if (enabled) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Ignore — best-effort.
      }
    }
    setQuery('');
  }, [enabled, key, setQuery]);

  return { query, setQuery, clearDraft };
}
